import type { NostrEvent } from '@nostrify/nostrify';

export interface SpamSettings {
  webOfTrust: {
    enabled: boolean;
    /** How far back to look for network reports/follows, in days */
    windowDays: number;
  };
  hashtag: {
    enabled: boolean;
    /** Hide posts with more than this many hashtag tags */
    maxTags: number;
  };
  speed: {
    enabled: boolean;
    /** Max events allowed within the look-back window */
    maxEvents: number;
    /** Rolling window for the speed filter, in minutes */
    windowMinutes: number;
  };
  readability: {
    enabled: boolean;
    /** Minimum length to treat a single token as base64 spam */
    minBase64Length: number;
  };
}

export const DEFAULT_SPAM_SETTINGS: SpamSettings = {
  webOfTrust: { enabled: true, windowDays: 30 },
  hashtag: { enabled: true, maxTags: 5 },
  speed: { enabled: true, maxEvents: 10, windowMinutes: 1 },
  readability: { enabled: true, minBase64Length: 24 },
};

const STORAGE_KEY = 'mixstr:spam-settings';

function storageKey(pubkey?: string): string {
  return pubkey ? `${STORAGE_KEY}:${pubkey}` : STORAGE_KEY;
}

export function loadSpamSettings(pubkey?: string): SpamSettings {
  try {
    const raw = localStorage.getItem(storageKey(pubkey));
    if (!raw) return DEFAULT_SPAM_SETTINGS;
    const parsed = JSON.parse(raw) as Partial<SpamSettings>;
    return mergeSpamSettings(parsed);
  } catch {
    return DEFAULT_SPAM_SETTINGS;
  }
}

export function saveSpamSettings(settings: SpamSettings, pubkey?: string): void {
  try {
    localStorage.setItem(storageKey(pubkey), JSON.stringify(settings));
  } catch {
    // ignore storage errors
  }
}

export function mergeSpamSettings(partial: Partial<SpamSettings>): SpamSettings {
  return {
    webOfTrust: { ...DEFAULT_SPAM_SETTINGS.webOfTrust, ...partial.webOfTrust },
    hashtag: { ...DEFAULT_SPAM_SETTINGS.hashtag, ...partial.hashtag },
    speed: { ...DEFAULT_SPAM_SETTINGS.speed, ...partial.speed },
    readability: { ...DEFAULT_SPAM_SETTINGS.readability, ...partial.readability },
  };
}

/** Kinds whose content is expected to be structured/empty and should never be readability-filtered. */
export const READABILITY_EXEMPT_KINDS = new Set<number>([
  0, // metadata
  3, // contacts
  6, // repost
  16, // generic repost
  20, // picture first-class
  1063, // file metadata
  30023, // long-form article
  30078, // app-specific data
  30311, // live stream
  31337, // audio track
  34235, // video event
  34236, // audio event (video wrapper)
  34550, // community definition
  4550, // community post approval (content is JSON-encoded original post)
  31922, // calendar event
  31923, // calendar event rsvp
  30315, // status
]);

export function isReadabilityExempt(event: NostrEvent): boolean {
  if (READABILITY_EXEMPT_KINDS.has(event.kind)) return true;
  // Trust explicit human-readable metadata tags
  if (event.tags.some(([t]) => t === 'title' || t === 'subject' || t === 'summary')) return true;
  return false;
}

export function looksLikeJson(content: string): boolean {
  const trimmed = content.trim();
  if (!(trimmed.startsWith('{') || trimmed.startsWith('['))) return false;
  if (content.length > 100_000) return false;
  try {
    JSON.parse(content);
    return true;
  } catch {
    return false;
  }
}

export function looksLikeBase64(content: string, minLength: number): boolean {
  // Never treat very short tokens as base64: English words like "test",
  // "this", etc. look base64 but are plainly human-readable. Most real
  // base64 spam is much longer, so enforce a sane floor even when the stored
  // setting is 0.
  const effectiveMin = Math.max(minLength, 24);
  if (content.length < effectiveMin) return false;
  if (/\s/.test(content)) return false;
  if (content.length % 4 !== 0) return false;
  if (!/^[A-Za-z0-9+/]+={0,2}$/.test(content)) return false;
  // For shorter tokens, insist on at least one digit or base64 symbol so we
  // don't accidentally hide a long dictionary word that happens to be a
  // multiple of four characters and has no spaces.
  if (content.length < 48 && !/[^A-Za-z]/.test(content)) return false;
  return true;
}

export function countHashtags(event: NostrEvent): number {
  return event.tags.filter(([t]) => t === 't').length;
}

/**
 * Returns true if the content appears to be machine-generated garbage rather
 * than human-readable text. Known structured/non-text kinds are exempted via
 * {@link isReadabilityExempt} before calling this.
 */
export function isNonHumanReadable(content: string, minBase64Length: number): boolean {
  if (looksLikeJson(content)) return true;
  if (looksLikeBase64(content, minBase64Length)) return true;
  return false;
}

/**
 * Builds a deterministic per-author speed index for a batch of events.
 *
 * For each pubkey, returns the maximum number of events that fall within any
 * rolling `windowMinutes` window. This lets callers flag accounts that posted
 * more than a configured threshold within a short period.
 *
 * The calculation is based on event `created_at` timestamps, not on when events
 * were received by the client. Timestamps in the future are clamped to the
 * current wall-clock time so that future-dated events do not inflate windows.
 */
export function buildSpeedIndex(
  events: NostrEvent[],
  windowMinutes: number,
): Map<string, number> {
  const windowSec = windowMinutes * 60;
  const nowSec = Math.floor(Date.now() / 1000);
  const byPubkey = new Map<string, number[]>();

  for (const event of events) {
    if (!event?.pubkey || typeof event.created_at !== 'number') continue;
    const ts = Math.min(event.created_at, nowSec);
    const list = byPubkey.get(event.pubkey) ?? [];
    list.push(ts);
    byPubkey.set(event.pubkey, list);
  }

  const index = new Map<string, number>();
  if (windowSec <= 0) return index;

  for (const [pubkey, timestamps] of byPubkey) {
    timestamps.sort((a, b) => a - b);
    let left = 0;
    let maxInWindow = 0;
    for (let right = 0; right < timestamps.length; right++) {
      while (timestamps[right] - timestamps[left] > windowSec) {
        left++;
      }
      const count = right - left + 1;
      if (count > maxInWindow) maxInWindow = count;
    }
    index.set(pubkey, maxInWindow);
  }

  return index;
}
